import type { InfiniteData } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';

import type { PaginatedResponse } from 'clients/types';
import type { Conversation } from 'components/ui/organisms/CentralInbox/CentralInbox.types';
import type { CandidateActivityLog } from './useCandidateActivityLogQuery.types';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { useGTM } from 'hooks/gtm/useGTM';
import { usePageContext } from 'hooks/shared/usePageContext';
import { CandidateHistoryAction, CandidateStatus } from 'services/candidates/types';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { QueryKey } from 'types/query';

type MarkAsReadParams = {
  esPersonId: string;
  status: CandidateStatus;
};

export const useMarkAsReadBasedOnEsPersonIdMutation = () => {
  const pageContext = usePageContext();
  const gtm = useGTM();
  const { isImpersonated } = useContext(UserContext);
  return useMutation(
    async (candidate: MarkAsReadParams) => {
      return await campaignsClient.markActivityLogAsReadBasedOnEsPersonId({
        es_person_id: candidate.esPersonId,
      });
    },
    {
      onSuccess: (_, candidate) => {
        if (isImpersonated) return;
        gtm.readMessage({
          pageContext,
          esPersonId: candidate.esPersonId,
        });
        queryClient.setQueryData(
          [
            QueryKey.useCandidateActivityLogQuery,
            { candidateEsPersonId: candidate.esPersonId, status: candidate.status },
          ],
          (data?: CandidateActivityLog[]) => {
            if (!data) return;
            return data.map((activity) => {
              if (activity.action !== CandidateHistoryAction.MessageReceived) return activity;

              return {
                ...activity,
                message: {
                  ...activity.message,
                  isNew: false,
                },
              };
            });
          },
        );

        queryClient.setQueriesData<InfiniteData<PaginatedResponse<Conversation>>>(
          {
            queryKey: [QueryKey.getInfiniteConversations],
            exact: false,
          },
          (data) => {
            if (!data) return data;
            return {
              pageParams: data.pageParams,
              pages: data.pages.map((page: PaginatedResponse<Conversation>) => {
                return {
                  ...page,
                  results: page.results.map((conversation: Conversation) => {
                    if (!conversation.candidate) return conversation;
                    if (conversation.esPersonId !== candidate.esPersonId) return conversation;

                    return { ...conversation, unreadCount: 0 };
                  }),
                };
              }),
            };
          },
        );

        queryClient.setQueryData([QueryKey.useGetCampaignNewRepliesQuery], (data: number | undefined) => {
          if (typeof data !== 'number') return;

          if (data > 0) return data - 1;
        });

        setTimeout(() => {
          queryClient.invalidateQueries([QueryKey.getInfiniteConversations], { exact: false, refetchType: 'none' });
          queryClient.invalidateQueries([QueryKey.useGetUnreadConversationsAmount], { exact: false });
          queryClient.invalidateQueries([QueryKey.campaignManagerContextUseCampaigns], { exact: false });
          queryClient.invalidateQueries([
            QueryKey.useCandidateActivityLogQuery,
            { candidateEsPersonId: candidate.esPersonId, status: candidate.status },
          ]);
        }, 3000);
      },
    },
  );
};
